import React, { useContext, useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Link } from 'react-router-dom';
import { Alert, Card, Empty, Spin, Table, Tag, theme, Tooltip } from 'antd';
import type { ApexOptions } from 'apexcharts';

import {
  isDarkBackground,
  sequentialColor,
  SEVERITY_KEYS,
  SEVERITY_LABELS,
  severityColors,
  trendColors,
} from '@totem/components/software/vulnerabilityReport/summary/chartColors';
import {
  DashboardFixFirst,
  DashboardUpgrade,
  SeverityCounts,
  VulnerabilityDashboard,
} from '@totem/components/software/vulnerabilityReport/summary/types';
import { VulnerabilityReportInput } from '@totem/components/software/vulnerabilityReport/types';
import { buildVulnerabilityReportFilters } from '@totem/components/software/vulnerabilityReport/utilities';
import VulnerabilityReportContext from '@totem/components/software/vulnerabilityReport/vulnerabilityReportContext';
import KpiCard from '@totem/components/ui_components/widgets/KpiCard';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { DEVICES_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  // Opens the Details tab with these filters applied on top of the shared filter bar.
  onDrillDown: (update: Partial<VulnerabilityReportInput>) => void;
};

const REASON_LABELS: Record<string, string> = {
  'version-not-comparable': "Installed version can't be compared",
  'no-range-data': 'CVE has no version data',
};

const STATUS_LABELS: Record<string, string> = {
  affected: 'Affected',
  review: 'Needs review',
};

const SEVERITY_DISPLAY: Record<string, keyof SeverityCounts> = {
  CRITICAL: 'critical',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
  '': 'none',
};

const formatPercent = (value: number) =>
  value >= 0.1
    ? `${(value * 100).toFixed(0)}%`
    : `${(value * 100).toFixed(1)}%`;

const VulnerabilitySummaryTab = ({ onDrillDown }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const { input } = useContext(VulnerabilityReportContext);
  const { token } = theme.useToken();
  const isDark = isDarkBackground(token.colorBgContainer);
  const severity = severityColors(isDark);

  const [dashboard, setDashboard] = useState<VulnerabilityDashboard | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [failed, setFailed] = useState<boolean>(false);

  // The summary shares the report's filters; a stable key avoids refetching when only paging or sorting changes.
  const filtersKey = JSON.stringify(buildVulnerabilityReportFilters(input));

  useEffect(() => {
    setLoading(true);

    setFailed(false);

    fetch(`${DEVICES_ENDPOINT}/software/vulnerabilityDashboard`, {
      method: 'POST',
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
      body: JSON.stringify({ filters: JSON.parse(filtersKey) }),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((result: VulnerabilityDashboard) => setDashboard(result))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [filtersKey, getApiToken]);

  const chartBase = useMemo(
    (): ApexOptions => ({
      chart: {
        toolbar: { show: false },
        animations: { enabled: false },
        background: 'transparent',
        fontFamily: 'inherit',
      },
      theme: { mode: isDark ? 'dark' : 'light' },
      grid: { borderColor: token.colorBorderSecondary },
      dataLabels: { enabled: false },
      legend: {
        position: 'bottom',
        fontSize: '11px',
        labels: { colors: token.colorTextSecondary },
      },
    }),
    [isDark, token.colorBorderSecondary, token.colorTextSecondary],
  );

  if (loading && !dashboard) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (failed || !dashboard) {
    return (
      <Alert
        type="error"
        title="The vulnerability summary could not be loaded."
      />
    );
  }

  const { kpis } = dashboard;
  const fixPercent =
    kpis.affected > 0
      ? Math.round((kpis.fixAvailable / kpis.affected) * 100)
      : 0;

  const severitySeries = (rows: SeverityCounts[]) =>
    SEVERITY_KEYS.map((key) => ({
      name: SEVERITY_LABELS[key],
      data: rows.map((row) => row[key]),
    })).filter((series) => series.data.some((value) => value > 0));

  const severitySeriesColors = (rows: SeverityCounts[]) =>
    SEVERITY_KEYS.filter((key) => rows.some((row) => row[key] > 0)).map(
      (key) => severity[key],
    );

  const stackedBar = (
    categories: string[],
    rows: SeverityCounts[],
    onSelect: (index: number) => void,
  ): ApexOptions => ({
    ...chartBase,
    chart: {
      ...chartBase.chart,
      type: 'bar',
      stacked: true,
      events: {
        dataPointSelection: (_event, _chart, config) =>
          onSelect(config.dataPointIndex),
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '62%',
        borderRadius: 4,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last',
      },
    },
    stroke: { width: 2, colors: [token.colorBgContainer] },
    colors: severitySeriesColors(rows),
    xaxis: {
      categories,
      labels: { style: { colors: token.colorTextSecondary } },
    },
    yaxis: {
      labels: {
        maxWidth: 220,
        style: { colors: token.colorTextSecondary, fontSize: '11px' },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      theme: isDark ? 'dark' : 'light',
    },
  });

  const packageRows = dashboard.topPackages.map((pkg) => pkg.severity);
  const packageCategories = dashboard.topPackages.map((pkg) =>
    pkg.knownExploited > 0
      ? `${pkg.packageName} (${pkg.knownExploited} exploited)`
      : pkg.packageName,
  );

  const statusRows = dashboard.statusBySeverity.map((row) => row.severity);
  const statusCategories = dashboard.statusBySeverity.map(
    (row) => STATUS_LABELS[row.status] ?? row.status,
  );

  const trendOptions: ApexOptions = {
    ...chartBase,
    chart: { ...chartBase.chart, type: 'line', zoom: { enabled: false } },
    colors: trendColors(isDark),
    stroke: { width: 2, curve: 'straight' },
    markers: { size: dashboard.trend.length < 15 ? 4 : 0 },
    xaxis: {
      type: 'datetime',
      labels: { style: { colors: token.colorTextSecondary } },
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: { style: { colors: token.colorTextSecondary } },
    },
    tooltip: {
      shared: true,
      intersect: false,
      x: { format: 'MMM d, yyyy' },
      theme: isDark ? 'dark' : 'light',
    },
  };
  const trendSeries = [
    {
      name: 'Affected',
      data: dashboard.trend.map((point) => ({
        x: point.date,
        y: point.affected,
      })),
    },
    {
      name: 'Known exploited',
      data: dashboard.trend.map((point) => ({
        x: point.date,
        y: point.knownExploited,
      })),
    },
    {
      name: 'Needs review',
      data: dashboard.trend.map((point) => ({
        x: point.date,
        y: point.needsReview,
      })),
    },
  ];

  const buildingOptions: ApexOptions = {
    ...chartBase,
    chart: {
      ...chartBase.chart,
      type: 'bar',
      events: {
        dataPointSelection: (_event, _chart, config) => {
          const building = dashboard.buildings[config.dataPointIndex];
          if (building) {
            onDrillDown({
              buildingId: [building.buildingId],
              status: ['affected'],
            });
          }
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '62%',
        borderRadius: 4,
        borderRadiusApplication: 'end',
      },
    },
    colors: [sequentialColor(isDark)],
    legend: { show: false },
    xaxis: {
      categories: dashboard.buildings.map(
        (building) => `${building.buildingName} (${building.devices})`,
      ),
      labels: { style: { colors: token.colorTextSecondary } },
    },
    yaxis: {
      labels: {
        maxWidth: 220,
        style: { colors: token.colorTextSecondary, fontSize: '11px' },
      },
    },
    tooltip: { theme: isDark ? 'dark' : 'light' },
  };

  const severityTag = (value: string) => {
    const key = SEVERITY_DISPLAY[value] ?? 'none';
    return (
      <span style={{ whiteSpace: 'nowrap' }}>
        <span
          style={{
            display: 'inline-block',
            width: 10,
            height: 10,
            borderRadius: 2,
            marginRight: 6,
            verticalAlign: -1,
            background: severity[key],
          }}
        />
        {SEVERITY_LABELS[key]}
      </span>
    );
  };

  const fixFirstColumns = [
    {
      title: 'CVE',
      dataIndex: 'vulnerability',
      key: 'vulnerability',
      render: (cve: string, record: DashboardFixFirst) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <Link to={`/dashboard/software/cve/${cve}`}>{cve}</Link>
          {record.knownExploited && (
            <Tooltip
              title={`Known exploited (CISA KEV). Federal remediation due ${record.kevDueDate}.`}
            >
              <Tag color="red" style={{ marginLeft: 6 }}>
                Exploited
              </Tag>
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: 'Software',
      dataIndex: 'packageName',
      key: 'packageName',
      ellipsis: true,
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      render: (value: string, record: DashboardFixFirst) => (
        <Tooltip title={`CVSS ${record.score}`}>{severityTag(value)}</Tooltip>
      ),
    },
    {
      title: (
        <Tooltip title="Probability of exploitation in the next 30 days (FIRST EPSS)">
          EPSS
        </Tooltip>
      ),
      dataIndex: 'epssScore',
      key: 'epssScore',
      align: 'right' as const,
      render: (value: number) => (value > 0 ? formatPercent(value) : '—'),
    },
    {
      title: 'Devices',
      dataIndex: 'devices',
      key: 'devices',
      align: 'right' as const,
    },
    {
      title: 'Fixed in',
      dataIndex: 'fixedVersion',
      key: 'fixedVersion',
      render: (value: string) => value || '—',
    },
  ];

  const upgradeColumns = [
    {
      title: 'Software',
      dataIndex: 'packageName',
      key: 'packageName',
      ellipsis: true,
    },
    { title: 'Installed', dataIndex: 'packageVersion', key: 'packageVersion' },
    {
      title: 'Upgrade to',
      dataIndex: 'upgradeTo',
      key: 'upgradeTo',
      render: (value: string) => <Tag>{value}</Tag>,
    },
    {
      title: 'CVEs cleared',
      dataIndex: 'cves',
      key: 'cves',
      align: 'right' as const,
    },
    {
      title: 'Devices',
      dataIndex: 'devices',
      key: 'devices',
      align: 'right' as const,
    },
  ];

  const cardStyles = { body: { padding: 12 } };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 10,
        }}
      >
        <KpiCard
          label="Affected findings"
          value={kpis.affected.toLocaleString()}
          sub={`${kpis.devices} devices · ${kpis.cves} CVEs · ${kpis.packages} packages`}
          onClick={() => onDrillDown({ status: ['affected'] })}
        />
        <KpiCard
          label="Known exploited"
          value={kpis.knownExploited.toLocaleString()}
          sub={`CISA KEV · ${kpis.knownExploitedCves} CVEs`}
          valueColor={kpis.knownExploited > 0 ? token.colorError : undefined}
          onClick={() =>
            onDrillDown({ status: ['affected'], knownExploited: ['true'] })
          }
        />
        <KpiCard
          label="Critical"
          value={kpis.critical.toLocaleString()}
          sub="CVSS 9.0 or higher"
          onClick={() =>
            onDrillDown({ status: ['affected'], severity: ['CRITICAL'] })
          }
        />
        <KpiCard
          label="Likely exploited"
          value={kpis.likelyExploited.toLocaleString()}
          sub="EPSS 10% or higher"
          onClick={() =>
            onDrillDown({
              status: ['affected'],
              sortField: 'epssScore',
              sortDirection: '-1',
            })
          }
        />
        <KpiCard
          label="Fix available"
          value={`${fixPercent}%`}
          sub={`${kpis.fixAvailable.toLocaleString()} of ${kpis.affected.toLocaleString()} have a fixed version`}
        />
        <KpiCard
          label="Needs review"
          value={kpis.needsReview.toLocaleString()}
          sub="Admin decision needed"
          onClick={() => onDrillDown({ status: ['review'] })}
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)',
          gap: 16,
        }}
      >
        <Card
          size="small"
          title="Top software by affected findings"
          extra={<span style={{ fontSize: 12 }}>Click a bar for details</span>}
          styles={cardStyles}
        >
          {dashboard.topPackages.length > 0 ? (
            <ReactApexChart
              type="bar"
              height={Math.max(220, dashboard.topPackages.length * 34 + 60)}
              options={stackedBar(packageCategories, packageRows, (index) => {
                const pkg = dashboard.topPackages[index];
                if (pkg) {
                  onDrillDown({
                    packageName: [pkg.packageName],
                    status: ['affected'],
                  });
                }
              })}
              series={severitySeries(packageRows)}
            />
          ) : (
            <Empty description="No affected software" />
          )}
        </Card>
        <Card
          size="small"
          title="Findings by status and severity"
          styles={cardStyles}
        >
          {statusRows.length > 0 ? (
            <ReactApexChart
              type="bar"
              height={170}
              options={stackedBar(statusCategories, statusRows, (index) => {
                const row = dashboard.statusBySeverity[index];
                if (row) {
                  onDrillDown({ status: [row.status] });
                }
              })}
              series={severitySeries(statusRows)}
            />
          ) : (
            <Empty description="No findings" />
          )}
          {dashboard.reviewReasons.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div
                style={{
                  fontSize: 12,
                  color: token.colorTextSecondary,
                  marginBottom: 6,
                }}
              >
                Why findings need review
              </div>
              {dashboard.reviewReasons.map((reason) => (
                <div
                  key={reason.reason}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 13,
                    padding: '3px 0',
                    borderBottom: `1px solid ${token.colorBorderSecondary}`,
                  }}
                >
                  <span>{REASON_LABELS[reason.reason] ?? reason.reason}</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {reason.count.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)',
          gap: 16,
        }}
      >
        <Card
          size="small"
          title="Fix first"
          extra={
            <span style={{ fontSize: 12 }}>
              Known exploited, then likelihood of exploitation, then CVSS
            </span>
          }
          styles={cardStyles}
        >
          <Table
            size="small"
            pagination={false}
            columns={fixFirstColumns}
            dataSource={dashboard.fixFirst}
            rowKey={(row) => `${row.vulnerability}-${row.packageId}`}
            onRow={(row: DashboardFixFirst) => ({
              onClick: () =>
                onDrillDown({
                  vulnerability: [row.vulnerability],
                  packageName: [row.packageName],
                  status: ['affected'],
                }),
              style: { cursor: 'pointer' },
            })}
          />
        </Card>
        <Card
          size="small"
          title="Upgrades that clear the most"
          extra={
            <span style={{ fontSize: 12 }}>
              Installed version → fixed version
            </span>
          }
          styles={cardStyles}
        >
          <Table
            size="small"
            pagination={false}
            columns={upgradeColumns}
            dataSource={dashboard.upgrades}
            rowKey={(row) => `${row.packageId}-${row.packageVersion}`}
            onRow={(row: DashboardUpgrade) => ({
              onClick: () =>
                onDrillDown({
                  packageName: [row.packageName],
                  packageVersion: [row.packageVersion],
                  status: ['affected'],
                }),
              style: { cursor: 'pointer' },
            })}
          />
        </Card>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)',
          gap: 16,
        }}
      >
        <Card
          size="small"
          title="Trend"
          extra={<span style={{ fontSize: 12 }}>Daily, last 90 days</span>}
          styles={cardStyles}
        >
          {dashboard.trend.length > 1 ? (
            <ReactApexChart
              type="line"
              height={260}
              options={trendOptions}
              series={trendSeries}
            />
          ) : (
            <Empty
              description={
                dashboard.trend.length === 1
                  ? `The trend starts ${dashboard.trend[0].date}; a point is added after each morning's refresh.`
                  : 'The trend starts after the next morning refresh.'
              }
            />
          )}
        </Card>
        <Card
          size="small"
          title="Exposure by building"
          extra={<span style={{ fontSize: 12 }}>Devices in brackets</span>}
          styles={cardStyles}
        >
          {dashboard.buildings.length > 0 ? (
            <ReactApexChart
              type="bar"
              height={Math.max(220, dashboard.buildings.length * 30 + 50)}
              options={buildingOptions}
              series={[
                {
                  name: 'Affected findings',
                  data: dashboard.buildings.map(
                    (building) => building.findings,
                  ),
                },
              ]}
            />
          ) : (
            <Empty description="No affected buildings" />
          )}
        </Card>
      </div>

      {dashboard.lastComputedAt && (
        <div
          style={{
            fontSize: 12,
            color: token.colorTextSecondary,
            textAlign: 'right',
          }}
        >
          Findings last refreshed{' '}
          {new Date(dashboard.lastComputedAt).toLocaleString()}
        </div>
      )}
    </div>
  );
};

export default VulnerabilitySummaryTab;
